import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { FileText, Download, Loader2, BookOpen, Eye, X } from 'lucide-react'
import { Manual, getActiveManuals, formatFileSize } from '../../services/manualService'

export default function UserManualPage() {
  const [manuals, setManuals] = useState<Manual[]>([])
  const [loading, setLoading] = useState(true)
  const [viewingPdf, setViewingPdf] = useState<{ url: string; title: string } | null>(null)

  useEffect(() => {
    loadManuals()
  }, [])

  const loadManuals = async () => {
    try {
      setLoading(true)
      const response = await getActiveManuals()
      setManuals(response.manuals)
    } catch (error) {
      toast.error('Error al cargar los manuales')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = (manual: Manual) => {
    const link = document.createElement('a')
    link.href = manual.fileUrl
    link.download = manual.fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success(`Descargando ${manual.fileName}`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manual de Usuario</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Consulta o descarga los manuales de uso de la aplicación
        </p>
      </div>

      {/* Lista de manuales */}
      {manuals.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-dark-card rounded-xl border border-gray-200 dark:border-dark-border">
          <BookOpen className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            No hay manuales disponibles
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Los manuales de usuario estarán disponibles próximamente
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {manuals.map((manual) => (
            <div
              key={manual.id}
              className="bg-white dark:bg-dark-card rounded-xl border border-gray-200 dark:border-dark-border p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{manual.title}</h3>
                  {manual.description && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{manual.description}</p>
                  )}
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                    <span>{manual.fileName}</span>
                    <span>{formatFileSize(manual.fileSize)}</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => setViewingPdf({ url: manual.fileUrl, title: manual.title })}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Eye className="w-5 h-5" />
                  Ver PDF
                </button>
                <button
                  onClick={() => handleDownload(manual)}
                  className="flex-1 btn btn-primary inline-flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Descargar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info adicional */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <div className="flex gap-3">
          <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300">
              Ayuda con la aplicación
            </h4>
            <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
              Si tienes dudas sobre cómo usar la aplicación, consulta el manual correspondiente
              a tu dispositivo (ordenador o móvil). Si necesitas ayuda adicional, contacta con
              el departamento de Recursos Humanos.
            </p>
          </div>
        </div>
      </div>

      {/* Modal para ver PDF */}
      {viewingPdf && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/70 transition-opacity"
              onClick={() => setViewingPdf(null)}
            />
            <div className="relative bg-white dark:bg-dark-card rounded-xl shadow-xl w-full max-w-5xl h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-border">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {viewingPdf.title}
                </h2>
                <button
                  onClick={() => setViewingPdf(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-hidden">
                <iframe
                  src={viewingPdf.url}
                  className="w-full h-full"
                  title={viewingPdf.title}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
